import { EntityStatusCard } from '@global-dax-ad-platform/dax-components';
import { NewTabIcon } from '@global-engineering-shared/gweb-material-icons-global-next';
import { Box, Button, Divider } from '@global-engineering-shared/gweb-material-ui';
import { Link } from 'react-router';

import { getEntityTitle } from 'features/Entity/components/EntityActionBar/utils/getEntityTitle';
import DetailsTitle from 'features/EntityDetails/components/EntityDetails/DetailsTitle';
import EntityDetail from 'features/EntityDetails/components/EntityDetails/EntityDetail';
import useStyles from 'features/EntityDetails/components/EntityDetails/EntityDetailsHeader/EntityDetailsHeader.styles';
import IdentifiableEntityDetail from 'features/EntityDetails/components/EntityDetails/IdentifiableEntityDetail';
import { TDraftFrameOrLinkedAsset, TLiveFrameOrLinkedAsset } from 'types/detailsEntityType';
import { EntityType } from 'types/entityType';
import { DRAFTS_PAGE_URL, FRAMES_PAGE_URL, LINKED_ASSETS_PAGE_URL } from 'utils/defaults';
import { DraftStatus, LiveStatus, statusColour, statusLabel } from 'utils/statuses';

type EntityDetailsHeaderProps<T> = {
  entity: T;
  previousEntity?: TLiveFrameOrLinkedAsset | null;
  backLink: string;
  linkLabel: string;
  entityLabel: string;
};

const EntityDetailsHeader = <T extends TDraftFrameOrLinkedAsset | TLiveFrameOrLinkedAsset>({
  entity,
  previousEntity,
  backLink,
  linkLabel,
  entityLabel,
}: EntityDetailsHeaderProps<T>) => {
  const classes = useStyles();

  const isDraftStatus = (status: LiveStatus | DraftStatus): status is DraftStatus => {
    return Object.values(DraftStatus).includes(status as DraftStatus);
  };
  const isLiveEntity = !isDraftStatus(entity.status);
  const liveEntityHasDraft = isLiveEntity && (entity as TLiveFrameOrLinkedAsset).hasDraft;
  const draftEntityHasLiveEntity = !isLiveEntity && previousEntity !== null;
  const isLinkedAsset = entity.entityType === EntityType.LINKED_ASSET;

  const livePageUrl = isLinkedAsset ? LINKED_ASSETS_PAGE_URL : FRAMES_PAGE_URL;
  const url = `${isLiveEntity ? DRAFTS_PAGE_URL : livePageUrl}/${entity.spaceId}`;

  return (
    <>
      <Box className={classes.header}>
        <Box>
          <DetailsTitle
            linkLabel={linkLabel}
            backLink={backLink}
            entityType={entity.entityType}
            entityName={entity.name}
            previousEntityName={previousEntity?.name}
          />
          <Box className={classes.details}>
            <EntityDetail label={`${entityLabel} ID`} value={entity.spaceId} />
            <Divider variant="dot" />
            <EntityDetail
              label="Media Owner"
              value={entity.mediaOwnerName}
              previousValue={previousEntity?.mediaOwnerName}
            />
            <Divider variant="dot" />
            <EntityDetail
              label="Environment"
              value={entity.environment}
              previousValue={previousEntity?.environment}
            />
            <Divider variant="dot" />
            <IdentifiableEntityDetail
              label="Format"
              entity={entity.formatSize}
              previousEntity={previousEntity?.formatSize}
            />
            {!isLiveEntity && (
              <>
                <Divider variant="dot" />
                <EntityDetail
                  label="Live Status"
                  value={(entity as TDraftFrameOrLinkedAsset).intendedLiveStatus}
                  previousValue={previousEntity?.status}
                />
              </>
            )}
          </Box>
        </Box>
        <Box>
          <EntityStatusCard
            entityType={getEntityTitle(entity.entityType, entity.status)}
            color={statusColour(entity.status)}
            subtitle={statusLabel(entity.status)}
          />
        </Box>
      </Box>
      <Divider />
      {(draftEntityHasLiveEntity || liveEntityHasDraft) && (
        <Box className={classes.buttonContainer}>
          <Link to={url}>
            <Button disableRipple variant="text">
              <Box className={classes.buttonInner}>
                <span>
                  {!isLiveEntity ? `See Live ${getEntityTitle(entity.entityType)}` : 'See Draft'}
                </span>
                <NewTabIcon className={classes.icon} />
              </Box>
            </Button>
          </Link>
        </Box>
      )}
    </>
  );
};

export default EntityDetailsHeader;
